// WHICH ID THE CLIENT SHOWED, AND ITS NUMBER WHERE A STATE'S RULE KEEPS IT (TW-0488, 2026-10-26). The pure half; the words are
// i18n.ts's, the page is Form.tsx's, the signing door's half is services/waiver-api/consolidated_api/id_type.py.
//
// Ana Ruiz of Squirtin' Ink (Florida), 2026-10-24: "We noticed with customers who are here on vacation from another county
// their ID is not valid even if it's a valid ID enough for us to tattoo them. We're not sure if there's a way to bypass that
// issue so it's easier for us." This page called every ID a driver's license: "Driver's License" over the camera, and "You
// must upload a photo of your driver's license." when the photo was missing, which refused a Submit 33 times at her shop from
// her first day (2026-09-23) to 2026-10-25 (`form_refused`, `no_license_photo`). Nothing ever read the photo, so a passport
// photographed there signed the same as a license; the words told a visitor with a passport the form wanted a thing they did
// not have.
//
// So the page asks for a photo of the client's ID, and which ID it is: a driver's license, a state ID, a passport or another
// government ID. The choice is OPTIONAL on every form and REQUIRED where the shop's state's rule keeps which ID on the record
// (the contract's `id_type`: Kansas, Texas, Maine, Missouri). The ID's NUMBER is asked, and required, where the rule keeps
// the number (`id_number`: Maine, Missouri), and it is TAKEN AS TYPED: never reshaped and never judged by its shape, because a
// passport or a license from another country has a number no list here knows. The photo is taken the same way for every
// one of them. The shop's eye on the ID is the check, as it always was; the photo is a copy for the file, never the proof of age.

/** The four codes the page posts as `idType`, in the order it offers them. The API's id_type.ID_TYPES holds the same four
 *  (its test reads this line). */
export const ID_TYPES = ["drivers_license", "state_id", "passport", "other"] as const;
export type IdType = (typeof ID_TYPES)[number];

export function isIdType(value: unknown): value is IdType {
  return typeof value === "string" && (ID_TYPES as readonly string[]).includes(value);
}

/** The contract's two `record` ids (scripts/state-forms.py VOCAB). */
export const TYPE_RECORD_ID = "id_type";
export const NUMBER_RECORD_ID = "id_number";

/** Must the client say which ID it is? Where the shop's state's rule keeps which ID (`id_type`), and where it keeps the ID's
 *  number (`id_number`), whose number is read by the type beside it. `required` is the state's ids (stateForms.ts
 *  requirementsFor). */
export function idTypeRequired(required: Set<string>): boolean {
  return required.has(TYPE_RECORD_ID) || required.has(NUMBER_RECORD_ID);
}

/** Does the page ask for the ID's number? Only where the shop's state's rule keeps it (`id_number`). */
export function idNumberAsked(required: Set<string>): boolean {
  return required.has(NUMBER_RECORD_ID);
}

/** The number as the client typed it, its runs of whitespace made one space and its ends trimmed. Never upper-cased,
 *  never reshaped: the API's id_type.number_posted reads it the same way. */
export function tidyIdNumber(value: string): string {
  return `${value ?? ""}`.split(/\s+/).filter(Boolean).join(" ");
}

// A letter or a digit, in any script. Built at run time so the es5 type-check never meets the flag, and with a fallback for a
// browser that cannot read a Unicode property: there, an ASCII letter or digit, or anything past Latin-1's punctuation, counts.
const LETTER_OR_DIGIT: RegExp = (() => {
  try {
    return new RegExp("[\\p{L}\\p{N}]", "u");
  } catch {
    return /[0-9A-Za-zÀ-￿]/;
  }
})();

/** Does this number carry anything a person could read back off the card: a letter or a digit, in any script? The one test
 *  a number is put to (the API's id_type.number_given). A box of spaces, dashes or dots is not a number; every shape with a
 *  letter or a digit in it is. */
export function idNumberGiven(value: string): boolean {
  return LETTER_OR_DIGIT.test(`${value ?? ""}`);
}

/** The keys the posted waiver gains, and only then: `idType` when the client picked one (or the rule required one, which the
 *  page's own gate makes sure of), `idNumber` wherever the page asked for it, tidied. A page that asked neither and a client
 *  who picked nothing post the body they posted yesterday. */
export function idFieldsPosted(a: { type: IdType | ""; number: string; typeRequired: boolean; numberAsked: boolean }): {
  idType?: IdType | "";
  idNumber?: string;
} {
  const out: { idType?: IdType | ""; idNumber?: string } = {};
  if (isIdType(a.type) || a.typeRequired) out.idType = isIdType(a.type) ? a.type : "";
  if (a.numberAsked) out.idNumber = tidyIdNumber(a.number);
  return out;
}
